import { useFpCampaignBillingService } from '@cart-services/hooks/useFpCampaignBillingService'
import { useFpCampaignCartService } from '@cart-services/hooks/useFpCampaignCartService'
import { CountryCode } from '@constants'
import { useApiUserAddressInfo } from '@hooks-api/ec/useApiUserAddressInfo'
import { useApiCampaignActivityInfoImmutable } from '@hooks-api/fortune/useApiCampaignActivityInfoImmutable'
import { type CheckoutDisplayFields } from '@hooks-api/types/dataModel/checkoutDisplayField'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { Divider } from '@pets-ui/Divider/Divider'
import { Drawer } from '@pets-ui/Drawer/Drawer'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { CloseLarge, NoticeOutlined } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { getParsedStorageItem, isEmail, STORAGE_EVENT } from '@utils'
import { parseState } from '@utils/parse-wp-state'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'
import CheckoutField from '../../components/checkout-field'
import CheckoutProgress from '../../components/checkout-progress'
import FixedFooter from '../../components/fixed-footer'
import Loading from '../../components/loading'
import { useCampaign } from '../../hooks/use-campaign'
import { useMember } from '../../hooks/use-member'
import style from '../../style/pages/client-checkout.module.scss'
import { useContextCampaignCart } from './cart/components/CartProvider/CampaignCartProvider'

const isEmptyDeliveryInfo = (deliveryInfo: DeliveryInfo | null) => {
  if (!deliveryInfo) return true
  // 因為 billing_country 都會需要給預設值，所以不會列入檢查
  return Object.entries(deliveryInfo).filter(([key]) => key !== 'billing_country').every(([_, value]) => value.value === '')
}

const isTwPhoneNumber = (phoneNumber: string) => {
  return /^09[0-9]{8}$/.test(phoneNumber)
}

type DeliveryInfo = Record<keyof CheckoutDisplayFields['display_text'], {
  isError: boolean
  value: string
  errorMessage: string
}>

const getDefaultDeliveryInfo = (fields: CheckoutDisplayFields['display_text']) => {
  const deliveryInfo = {} as DeliveryInfo
  Object.keys(fields).forEach((key) => {
    const field = key as keyof CheckoutDisplayFields['display_text']
    deliveryInfo[field] = {
      isError: false,
      errorMessage: '',
      value: '',
    }
  })

  return deliveryInfo
}

export const ClientCheckout = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const sessionStorageDelivery = getParsedStorageItem<DeliveryInfo>('deliveryInfo', STORAGE_EVENT.sessionStorage)
  const { campaignCodeFromUrl } = useCampaign()
  const { countryCode, projectCode } = useContextCampaignCart()
  const [deliveryInfo, setDeliveryInfo] = useState<DeliveryInfo | null>()
  const [isDeliveryInfoInitialized, setIsDeliveryInfoInitialized] = useState(false)
  const { destructuredData: exhibitorSettings, isLoading: isActivityLoading } = useApiCampaignActivityInfoImmutable(campaignCodeFromUrl)
  const { checkoutFields, isLoading: isCheckoutFieldsLoading, setBillingCountry, setBilling } = useFpCampaignBillingService(campaignCodeFromUrl, exhibitorSettings)
  const { member, hasToken } = useMember()
  const { destructuredData: userAddressInfo } = useApiUserAddressInfo(member ? { country_code: countryCode, project_code: projectCode } : undefined, {
    onSuccess: () => {
      setIsDeliveryInfoInitialized(false)
    },
  })
  const parsedState = parseState(userAddressInfo?.state, countryCode)
  const { items: cartItems } = useFpCampaignCartService(exhibitorSettings?.campaign_id ?? null)
  const isUS = deliveryInfo?.billing_country?.value as CountryCode === CountryCode.US
  const isTW = deliveryInfo?.billing_country?.value as CountryCode === CountryCode.TW
  const isHK = deliveryInfo?.billing_country?.value as CountryCode === CountryCode.HK
  const cities = useMemo(() => (
    checkoutFields?.available_states?.[
      deliveryInfo?.billing_address_state?.value as string
    ]?.map(city => ({
      key: city,
      value: city,
      display: isTW ? city.split(' ')[1] : city,
    })) || []
  ), [checkoutFields?.available_states, deliveryInfo?.billing_address_state?.value, isTW])

  useEffect(() => {
    if (!deliveryInfo) {
      return
    }

    let value = cities[0]?.value || ''
    const cityValues = Object.values(cities).map(city => city.value)
    if (
      deliveryInfo.billing_address_city?.value
      && cityValues.includes(deliveryInfo.billing_address_city?.value)
      && !isUS
    ) {
      value = deliveryInfo.billing_address_city.value
    }
    if (isTW || isHK) {
      setDeliveryInfo({
        ...deliveryInfo,
        billing_address_city: {
          isError: false,
          value,
          errorMessage: '',
        },
      })
    }
    // NOTE: Don't add deliveryInfo to deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities, isHK, isTW, isUS])

  const toPaymentPage = () => {
    history.push(`/campaigns/${campaignCodeFromUrl}/payments`)
  }

  const handleSubmit = () => {
    const newDeliveryInfo = { ...deliveryInfo } as unknown as DeliveryInfo
    let hasError = false

    Object.entries(fields || {}).forEach(([key, value]) => {
      const field = key as keyof CheckoutDisplayFields['display_text']
      if (!newDeliveryInfo[field]?.value && value.is_required) {
        hasError = true
        newDeliveryInfo[field] = {
          isError: true,
          errorMessage: t('order.requiredFieldErrorMessage'),
          value: '',
        }
      }

      if (field === 'billing_address_phone') {
        const phoneNumber = newDeliveryInfo[field]?.value
        if (phoneNumber && isTW && !isTwPhoneNumber(phoneNumber)) {
          hasError = true
          newDeliveryInfo[field] = {
            isError: true,
            errorMessage: '手機號碼格式錯誤！',
            value: phoneNumber,
          }
        }
      }

      if (field === 'billing_address_email') {
        const email = newDeliveryInfo[field]?.value
        if (email && !isEmail(email)) {
          hasError = true
          newDeliveryInfo[field] = {
            isError: true,
            errorMessage: '電子信箱格式錯誤！',
            value: email,
          }
        }
      }

      if (isUS && field === 'billing_address_postcode') {
        const includesZip = cities.find(city => Number(city.value) === Number(newDeliveryInfo[field]?.value))
        hasError = !includesZip
        if (!newDeliveryInfo[field].isError) {
          newDeliveryInfo[field] = {
            isError: !includesZip,
            errorMessage: 'Please enter a valid ZIP or postal code',
            value: newDeliveryInfo[field]?.value,
          }
        }
      }
    })

    if (hasError) {
      setDeliveryInfo(newDeliveryInfo)
      return
    }

    const formattedDeliveryInfo = Object.keys(deliveryInfo as DeliveryInfo).reduce<Record<string, string>>((acc, next) => {
      const value = deliveryInfo?.[next as keyof DeliveryInfo]?.value as string
      acc[next] = value
      return acc
    }, {})

    setBilling({
      billingCountry: deliveryInfo?.billing_country?.value as CountryCode,
      billingAddressCity: formattedDeliveryInfo.billing_address_city ?? '',
      billingAddressState: formattedDeliveryInfo.billing_address_state ?? '',
      billingAddressPostcode: formattedDeliveryInfo.billing_address_postcode ?? '',
      billingAddressPhone: formattedDeliveryInfo.billing_address_phone ?? '',
      billingAddressEmail: formattedDeliveryInfo.billing_address_email ?? '',
      billingAddressAddress1: formattedDeliveryInfo.billing_address_address1 ?? '',
      billingAddressAddress2: formattedDeliveryInfo.billing_address_address2 ?? '',
      billingAddressFirstName: formattedDeliveryInfo.billing_address_first_name ?? '',
      customerNote: formattedDeliveryInfo.customer_note ?? '',
    })

    toPaymentPage()
  }

  useEffect(() => {
    function syncDeliveryInfoToSessionStorage() {
      if (deliveryInfo) {
        sessionStorage.setItem('deliveryInfo', JSON.stringify(deliveryInfo))
      }
    }
    syncDeliveryInfoToSessionStorage()
  }, [deliveryInfo])

  const deliveryInfoWithDefault = useMemo(() => {
    if (checkoutFields) {
      const defaultDeliveryInfo = getDefaultDeliveryInfo(checkoutFields.display_text)
      return {
        ...defaultDeliveryInfo,
        billing_country: {
          isError: false,
          value: exhibitorSettings?.available_regions?.[0] || 'TW',
          errorMessage: '',
        },
      }
    }
  }, [checkoutFields, exhibitorSettings?.available_regions])

  const deliveryInfoWithMember = useMemo(() => {
    if (userAddressInfo && checkoutFields) {
      const defaultDeliveryInfo = getDefaultDeliveryInfo(checkoutFields.display_text)
      return {
        ...defaultDeliveryInfo,
        billing_address_address1: {
          isError: false,
          value: userAddressInfo.address_1,
          errorMessage: '',
        },
        billing_address_address2: {
          isError: false,
          value: userAddressInfo.address_2,
          errorMessage: '',
        },
        billing_address_city: {
          isError: false,
          value: parsedState.city,
          errorMessage: '',
        },
        billing_address_state: {
          isError: false,
          value: parsedState.state,
          errorMessage: '',
        },
        billing_address_postcode: {
          isError: false,
          value: parsedState.postcode,
          errorMessage: '',
        },
        billing_address_email: {
          isError: false,
          value: userAddressInfo.email,
          errorMessage: '',
        },
        billing_address_first_name: {
          isError: false,
          value: userAddressInfo.first_name,
          errorMessage: '',
        },
        billing_address_phone: {
          isError: false,
          value: userAddressInfo.phone,
          errorMessage: '',
        },
        billing_country: {
          isError: false,
          value: exhibitorSettings?.available_regions?.[0] || 'TW',
          errorMessage: '',
        },
      }
    }
  }, [checkoutFields, exhibitorSettings?.available_regions, parsedState.city, parsedState.postcode, parsedState.state, userAddressInfo])

  useEffect(() => {
    if (isDeliveryInfoInitialized) return
    initDeliveryInfo()
    function initDeliveryInfo() {
      if (hasToken && deliveryInfoWithMember && isEmptyDeliveryInfo(sessionStorageDelivery ?? null)) {
        setDeliveryInfo(deliveryInfoWithMember)
        sessionStorage.setItem('deliveryInfo', JSON.stringify(deliveryInfoWithMember))
        setIsDeliveryInfoInitialized(true)
        return
      }
      if (sessionStorageDelivery) {
        setDeliveryInfo(sessionStorageDelivery)
        setIsDeliveryInfoInitialized(true)
        return
      }
      if (deliveryInfoWithDefault) {
        setDeliveryInfo(deliveryInfoWithDefault)
        setIsDeliveryInfoInitialized(true)
      }
    }
  }, [deliveryInfoWithMember, deliveryInfoWithDefault, hasToken, sessionStorageDelivery, isDeliveryInfoInitialized])

  useEffect(() => {
    function initializeBillingCountry() {
      if (!isDeliveryInfoInitialized || !deliveryInfo?.billing_country?.value) return
      const countryValue = deliveryInfo.billing_country.value as CountryCode
      setBillingCountry(countryValue)
    }
    initializeBillingCountry()
  }, [deliveryInfo?.billing_country?.value, isDeliveryInfoInitialized, setBillingCountry])

  const fields = checkoutFields?.display_text
  const regionOptions = exhibitorSettings?.available_regions?.map((key) => {
    return {
      key,
      value: t(`country.${key}`) || '',
    }
  })

  const availableStates = checkoutFields?.available_states || {}
  const states = Object.keys(availableStates).map((key) => {
    return {
      key,
      value: key,
    }
  })

  const checkoutFieldsOrder = exhibitorSettings && checkoutFields?.display_text_order

  const fieldComponents
    = (
      <>
        {fields && checkoutFieldsOrder
          ?.filter((key): key is keyof CheckoutDisplayFields['display_text'] => !!fields?.[key as unknown as keyof CheckoutDisplayFields['display_text']])
          ?.map((key) => {
            const field = key
            const props = {
              name: field,
              displayName: fields[field].display_name,
              value: deliveryInfo?.[field]?.value || '',
              isRequired: fields[field].is_required,
              isError: deliveryInfo?.[field]?.isError || false,
              errorMessage: deliveryInfo?.[field]?.errorMessage || '',
              onChange: (
                e: React.ChangeEvent<
                HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
                >,
              ) => {
                setDeliveryInfo({
                  ...deliveryInfo,
                  [field]: { isError: false, value: e.target.value },
                } as DeliveryInfo)
              },
            }

            if (field === 'billing_country') {
              return (
                <CheckoutField
                  {...props}
                  key={field}
                  type="select"
                  options={regionOptions as { key: string, value: string }[]}
                  onChange={(e) => {
                    props.onChange(e)
                    setBillingCountry(e.target.value as CountryCode)
                  }}
                />
              )
            }

            if (field === 'customer_note') {
              return (
                <CheckoutField
                  key={field}
                  type="textarea"
                  placeholder={fields[field].placeholder}
                  {...props}
                />
              )
            }

            if (field === 'billing_address_state') {
              return (
                <CheckoutField
                  key={field}
                  type="select"
                  options={states}
                  placeholder={fields[field].placeholder}
                  {...props}
                />
              )
            }

            if (field === 'billing_address_city' && !isUS) {
              return (
                <CheckoutField
                  key={field}
                  type="select"
                  options={cities}
                  {...props}
                  placeholder={fields[field].placeholder}
                />
              )
            }

            if (field === 'billing_address_postcode' && isUS) {
              return (
                <CheckoutField
                  key={field}
                  {...props}
                  type="input"
                  placeholder="e.g., 52001"
                  label={(
                    <HStack>
                      <Text>{fields[field].display_name}</Text>
                      <NoticeOutlined onClick={() => setIsOpen(true)} />
                    </HStack>
                  )}
                />
              )
            }

            return (
              <CheckoutField
                key={field}
                type="input"
                placeholder={fields[field].placeholder}
                {...props}
              />
            )
          })}
      </>
    )

  useEffect(() => {
    if (cartItems.length === 0) {
      history.push(`/campaigns/${campaignCodeFromUrl}/cart`)
    }
  }, [campaignCodeFromUrl, cartItems, history])

  const isLoading = isCheckoutFieldsLoading || isActivityLoading

  return (
    <main className={style.main}>
      <CheckoutProgress currentStep={1} />
      {isLoading
        ? <Loading />
        : (
          <>
            <div className={style.form}>
              <h2 className={style.title}>{t('global.shippingInformation')}</h2>
              {fieldComponents}
            </div>
            <FixedFooter classes={style.footer}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  history.push(`/campaigns/${campaignCodeFromUrl}/cart`)
                }}
              >
                {t('button.return')}
              </Button>
              <Button
                fullWidth
                onClick={handleSubmit}
              >
                {t('button.next')}
              </Button>
            </FixedFooter>
            <Drawer direction="bottom" open={isOpen} onClose={() => setIsOpen(false)}>
              <HStack justifyContent="space-between" padding={4}>
                <Text variant="h2">Notification</Text>
                <IconButton onClick={() => setIsOpen(false)}>
                  <CloseLarge />
                </IconButton>
              </HStack>
              <Divider />
              <Box padding={4}>
                <Text>
                  Please note that the order total during checkout reflects the estimated sales tax. The actual charge to your credit card will include the applicable state and local sales tax.
                </Text>
              </Box>
            </Drawer>
          </>
        )}
    </main>
  )
}
